import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import apiService from '../services/api';
import { useAppContext } from '../context/AppContext';

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 13); // 14 days inclusive
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

const formatXAxisLabel = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
};

const DataKpiPage = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [chartData, setChartData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { t } = useAppContext();

  const fetchKpi = useCallback(async () => {
    if (!startDate || !endDate || startDate > endDate) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getProgramKpi(startDate, endDate);
      const mapped = response.data.map(d => ({
        date: d.date,
        label: formatXAxisLabel(d.date),
        Updated: d.count,
      }));
      setChartData(mapped);
      setTotal(response.total);
    } catch (err) {
      setError(t('dataKpi.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, t]);

  useEffect(() => {
    fetchKpi();
  }, [fetchKpi]);

  const dayCount = (() => {
    if (!startDate || !endDate) return 0;
    const diff = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
    return Math.round(diff) + 1;
  })();

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
        <h1 className="text-base font-semibold text-gray-800">{t('dataKpi.title')}</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">{t('dataKpi.from')}:</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">{t('dataKpi.to')}:</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
          <span className="text-xs text-gray-400">
            {startDate} {t('dataKpi.to')} {endDate} ({dayCount} {t('dataKpi.days')})
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <span className="text-sm text-gray-400">{t('dataKpi.loading')}</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="p-4 rounded bg-red-50 border border-red-200 text-sm text-red-600">
            {error}
          </div>
        )}

        {!isLoading && !error && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            {/* Chart section header */}
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700">
                {t('dataKpi.sectionTitle')}
              </h2>
              <div className="text-xs text-gray-600">
                {t('dataKpi.updatedTotal')}:{' '}
                <span className="font-semibold text-blue-600">{total.toLocaleString()}</span>
              </div>
            </div>

            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-sm text-gray-400">
                {t('dataKpi.noData')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    interval="preserveStartEnd"
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    formatter={(value) => [value.toLocaleString(), t('dataKpi.updated')]}
                    labelStyle={{ fontSize: 12, color: '#374151' }}
                    contentStyle={{ fontSize: 12, borderRadius: '6px', border: '1px solid #e5e7eb' }}
                  />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 12, paddingTop: '12px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Updated"
                    name={t('dataKpi.updated')}
                    stroke="#3b82f6"
                    strokeWidth={1.5}
                    dot={{ r: 3, fill: '#3b82f6', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataKpiPage;
